// Ported from hotwire-flow (MIT, @hotwirebits/flow) into conek-ui. The engine
// logic is upstream's; identifiers/events/classes follow the conek contract in
// docs/superpowers/specs/2026-09-09-flow-graph-components-design.md §2.
import { Controller } from "@hotwired/stimulus"
// The preview draws the SAME shape family the rendered edges draw, so both
// consume the one shared math module (edge_controller.js imports it too).
// Relative import: gem JS must not use host aliases.
import {
  getBezierPath as bezierPath,
  getSmoothStepPath as smoothStepPath,
  getStraightPath as straightPath
} from "../../../lib/flow-edge-paths.js"

// The canvas-root `data-connection-line-type` spellings the preview honors —
// the same set canvas_component.rb can emit (flow_controller declares the
// value; connectionLineTypeValue itself is unread, the component's attribute
// is the live projection hosts configure through).
const CONNECTION_LINE_TYPES = ["bezier", "smoothstep", "step", "straight"]
// The four positional handle sides the shared path math accepts.
const HANDLE_SIDES = ["top", "bottom", "left", "right"]
// React Flow parity: the free (pointer-chasing) end of the preview approaches
// from the side OPPOSITE the fixed handle's side.
const OPPOSITE_SIDE = { top: "bottom", bottom: "top", left: "right", right: "left" }

// Temporary connection line shown while dragging between handles.
// Listens for custom events instead of using outlets.
export default class extends Controller {
  connect() {
    this.sourcePosition = null
    this.targetPosition = null
    this.sourceSide = null
    this.element.classList.add("ui-flow-connection-line")
    // The `d` attribute belongs on the path child, not the svg root —
    // setting it on `this.element` renders nothing (UIUX-RF blocker).
    this._path = this.element.querySelector("path") || this.element

    // Multi-canvas isolation (wave-8 A4): the engine emits connectionstart/
    // move/end/cancel on its OWN canvas root (bubbling), so at document level
    // a drag on canvas A also reached canvas B's line — a phantom animated
    // line over B. Each handler drops events whose Element target sits
    // outside this line's own canvas root. A null root (standalone svg
    // outside any canvas) keeps the document-dispatch escape hatch working.
    this._ownGesture = (e) => {
      if (!(e.target instanceof Element)) return true
      const root = this.element.closest("[data-controller~='ui--flow']")
      return !root || root.contains(e.target)
    }
    this._onStart = (e) => {
      if (!this._ownGesture(e)) return
      // m2: connectionstart's detail.position is supposed to be {x, y} flow
      // coords, but the engine's reconnect path passes the handle SIDE string
      // ("right") — destructuring that drew "M undefined undefined" paths.
      // Coerce: valid point → anchor; anything else → let the first
      // connectionmove anchor both ends (it already back-fills a null source).
      // A side string doubles as the drag's fixed-handle side: the shared path
      // math needs it to aim the free end (connectionLineType fix below).
      this.sourceSide = this._coerceSide(e.detail?.position)
      this.sourcePosition = this._coercePoint(e.detail?.position)
      this.targetPosition = this.sourcePosition ? { ...this.sourcePosition } : null
      this.element.style.display = "block"
    }
    this._onMove = (e) => {
      if (!this._ownGesture(e)) return
      this.sourcePosition = this.sourcePosition || { x: e.detail.x, y: e.detail.y }
      this.targetPosition = { x: e.detail.x, y: e.detail.y }
      this._render()
    }
    this._onEnd = (e) => {
      if (!this._ownGesture(e)) return
      this.element.style.display = "none"
      this._path.removeAttribute("d")
      this.sourcePosition = null
      this.targetPosition = null
      this.sourceSide = null
    }

    document.addEventListener("ui--flow:connectionstart", this._onStart)
    document.addEventListener("ui--flow:connectionmove", this._onMove)
    document.addEventListener("ui--flow:connectionend", this._onEnd)
    document.addEventListener("ui--flow:connectioncancel", this._onEnd)
  }

  disconnect() {
    document.removeEventListener("ui--flow:connectionstart", this._onStart)
    document.removeEventListener("ui--flow:connectionmove", this._onMove)
    document.removeEventListener("ui--flow:connectionend", this._onEnd)
    document.removeEventListener("ui--flow:connectioncancel", this._onEnd)
  }

  _render() {
    if (!this.sourcePosition || !this.targetPosition) return
    const { x: sx, y: sy } = this.sourcePosition
    const { x: tx, y: ty } = this.targetPosition
    // Fixed end's handle side: the engine's connectionstart carries it as a
    // side string; a point-only start (or none) falls back to the edge
    // controller's own default pair (right → left).
    const sourceSide = this.sourceSide || "right"
    const targetSide = OPPOSITE_SIDE[sourceSide] || "left"

    let path
    switch (this._connectionLineType()) {
      case "straight":
        path = straightPath(sx, sy, tx, ty)[0]
        break
      case "step":
        path = smoothStepPath(sx, sy, sourceSide, tx, ty, targetSide, 0, 20)[0]
        break
      case "smoothstep":
        path = smoothStepPath(sx, sy, sourceSide, tx, ty, targetSide, 5, 20)[0]
        break
      default: // bezier — and the fallback for an absent/unknown attribute
        path = bezierPath(sx, sy, sourceSide, tx, ty, targetSide, 0.25)[0]
        break
    }
    this._path.setAttribute("d", path)
  }

  // The canvas's connection line type, read from the canvas root the way
  // _ownGesture scopes gestures (this line is an event listener, not an
  // outlet consumer). canvas_component.rb emits `data-connection-line-type`
  // on the root when the host passes a non-default value; anything absent or
  // unknown keeps the historical bezier.
  _connectionLineType() {
    const root = this.element.closest("[data-controller~='ui--flow']")
    const type = root?.getAttribute("data-connection-line-type")
    return CONNECTION_LINE_TYPES.includes(type) ? type : "bezier"
  }

  // A usable handle SIDE: one of the four positional strings. Semantic handle
  // ids are NOT a side — they fall back to the default pair like a non-side
  // start position does.
  _coerceSide(p) {
    return HANDLE_SIDES.includes(p) ? p : null
  }

  // A usable anchor point: an {x, y} object with finite numbers. Strings are
  // given one JSON.parse chance (a serialized point); anything else — including
  // the handle-side strings the reconnect path sends — is not a point.
  _coercePoint(p) {
    if (typeof p === "string") {
      try { p = JSON.parse(p) } catch (e) { return null }
    }
    if (p && Number.isFinite(p.x) && Number.isFinite(p.y)) return { x: p.x, y: p.y }
    return null
  }
}
